import { useCallback, useEffect, useRef, useState } from "react";
import type { ApiSmStateSnapshot } from "@bridge-applications/pb-types";
import {
    keepPlayingOfferAvailability,
    keepPlayingRefill,
} from "../helpers/keepPlayingOffer";
import { haptics } from "../helpers/haptics";
import { hasSpinEnergy } from "../helpers/slotMachineState";
import type { CommerceController } from "./useCommerce";

export function useKeepPlayingOffer(
    commerce: CommerceController,
    slotSnapshot: ApiSmStateSnapshot | null,
) {
    const {
        activePresentation,
        recordPresentation,
        dismissPresentation,
        resetCheckout,
        purchaseOffer,
    } = commerce;
    const presentation =
        activePresentation?.targetKind === "OFFER" ? activePresentation : null;
    const isOpen = Boolean(presentation?.autoOpen);
    const [now, setNow] = useState(Date.now);
    const purchasePendingRef = useRef(false);

    useEffect(() => {
        if (!isOpen) return;
        setNow(Date.now());
        const timer = window.setInterval(() => setNow(Date.now()), 1_000);
        return () => window.clearInterval(timer);
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && presentation) void recordPresentation(presentation);
    }, [isOpen, presentation, recordPresentation]);

    const close = useCallback(() => {
        if (!presentation) return;
        dismissPresentation(presentation.presentationId);
        resetCheckout();
    }, [dismissPresentation, presentation, resetCheckout]);

    useEffect(() => {
        // A confirmed free refill also lets the player continue. The countdown
        // itself never grants energy or dismisses an offer before that response.
        if (isOpen && hasSpinEnergy(slotSnapshot)) close();
    }, [close, isOpen, slotSnapshot]);

    const isBusy =
        commerce.mutationStatus !== "idle" ||
        commerce.checkoutStatus === "pending" ||
        (commerce.snapshot === null &&
            commerce.loadStatus !== "error" &&
            commerce.loadStatus !== "disabled");
    const availability = presentation && commerce.snapshot
        ? keepPlayingOfferAvailability(presentation.offer, now)
        : "unavailable";
    const purchase = useCallback(async () => {
        if (
            !presentation ||
            !commerce.snapshot ||
            isBusy ||
            purchasePendingRef.current ||
            keepPlayingOfferAvailability(presentation.offer, Date.now()) !==
                "available"
        )
            return;
        purchasePendingRef.current = true;
        haptics.impact("light");
        try {
            const status = await purchaseOffer(presentation.offer);
            if (status === "paid") {
                haptics.notification("success");
                close();
            } else if (status === "failed") {
                haptics.notification("error");
            }
        } catch {
            // The commerce controller supplies the localized error and releases
            // its checkout lock, so the same offer can be retried safely.
            haptics.notification("error");
        } finally {
            purchasePendingRef.current = false;
        }
    }, [close, commerce.snapshot, isBusy, presentation, purchaseOffer]);

    return {
        isOpen,
        offer: presentation?.offer ?? null,
        availability,
        isBusy,
        error: commerce.error,
        refill: keepPlayingRefill(slotSnapshot, now),
        close,
        purchase,
    };
}
